/**
 * HELPER FUNCTIONS - MALA HIERBA
 * Funciones utilitarias reutilizables
 */

/**
 * Formatear tiempo en formato MM:SS
 * @param {number} seconds - Segundos totales
 * @returns {string} Tiempo formateado
 */
export function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Obtener color según nivel de integridad
 * @param {number} integrity - Porcentaje de integridad (0-100)
 * @returns {string} Color hex
 */
export function getIntegrityColor(integrity) {
  if (integrity > 70) return '#48bb78'; // Verde
  if (integrity >= 40) return '#f6ad55'; // Amarillo
  return '#dc2626'; // Rojo
}

/**
 * Generar posición aleatoria dentro de un área
 * @param {number} minX - X mínimo
 * @param {number} maxX - X máximo
 * @param {number} minY - Y mínimo
 * @param {number} maxY - Y máximo
 * @returns {object} {x, y}
 */
export function randomPosition(minX, maxX, minY, maxY) {
  return {
    x: Phaser.Math.Between(minX, maxX),
    y: Phaser.Math.Between(minY, maxY)
  };
}

/**
 * Seleccionar elemento aleatorio de un array
 * @param {Array} array - Array de elementos
 * @returns {*} Elemento aleatorio
 */
export function randomElement(array) {
  return array[Phaser.Math.Between(0, array.length - 1)];
}

/**
 * Interpolar entre dos valores
 * @param {number} start - Valor inicial
 * @param {number} end - Valor final
 * @param {number} t - Factor de interpolación (0-1)
 * @returns {number} Valor interpolado
 */
export function lerp(start, end, t) {
  return start + (end - start) * t;
}

/**
 * Limitar un valor entre min y max
 * @param {number} value - Valor a limitar
 * @param {number} min - Mínimo
 * @param {number} max - Máximo
 * @returns {number} Valor limitado
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Reemplazar placeholders en string con valores
 * @param {string} template - String con {placeholders}
 * @param {object} values - Objeto con valores {key: value}
 * @returns {string} String con valores reemplazados
 */
export function fillTemplate(template, values) {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key] !== undefined ? values[key] : match;
  });
}

/**
 * Crear efecto de shake/vibración
 * @param {Phaser.GameObjects.GameObject} obj - Objeto a animar
 * @param {Phaser.Scene} scene - Escena de Phaser
 * @param {number} intensity - Intensidad del shake
 * @param {number} duration - Duración en ms
 */
export function shake(obj, scene, intensity = 5, duration = 300) {
  const originalX = obj.x;
  const originalY = obj.y;
  
  scene.tweens.add({
    targets: obj,
    x: originalX + Phaser.Math.Between(-intensity, intensity),
    y: originalY + Phaser.Math.Between(-intensity, intensity),
    duration: 50,
    yoyo: true,
    repeat: Math.floor(duration / 100),
    onComplete: () => {
      obj.x = originalX;
      obj.y = originalY;
    }
  });
}

/**
 * Crear efecto de pulso
 * @param {Phaser.GameObjects.GameObject} obj - Objeto a animar
 * @param {Phaser.Scene} scene - Escena de Phaser
 * @param {number} scale - Escala máxima
 * @param {number} duration - Duración en ms
 */
export function pulse(obj, scene, scale = 1.2, duration = 200) {
  const originalScale = obj.scale;
  
  scene.tweens.add({
    targets: obj,
    scale: scale,
    duration: duration / 2,
    yoyo: true,
    onComplete: () => {
      obj.scale = originalScale;
    }
  });
}

/**
 * Verificar si es dispositivo móvil
 * @returns {boolean}
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Obtener ancho de pantalla apropiado
 * @returns {number}
 */
export function getGameWidth() {
  if (isMobile()) {
    return Math.min(window.innerWidth, 428);
  }
  return 800;
}

/**
 * Obtener alto de pantalla apropiado
 * @returns {number}
 */
export function getGameHeight() {
  if (isMobile()) {
    return window.innerHeight;
  }
  return 600;
}

/**
 * Guardar en localStorage de forma segura
 * @param {string} key - Clave
 * @param {*} value - Valor (se convertirá a JSON)
 */
export function saveToStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error('Error guardando en localStorage:', e);
    return false;
  }
}

/**
 * Cargar de localStorage de forma segura
 * @param {string} key - Clave
 * @param {*} defaultValue - Valor por defecto si no existe
 * @returns {*} Valor parseado o defaultValue
 */
export function loadFromStorage(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.error('Error cargando de localStorage:', e);
    return defaultValue;
  }
}

/**
 * Borrar de localStorage
 * @param {string} key - Clave
 */
export function removeFromStorage(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.error('Error borrando de localStorage:', e);
    return false;
  }
}

export default {
  formatTime,
  getIntegrityColor,
  randomPosition,
  randomElement,
  lerp,
  clamp,
  fillTemplate,
  shake,
  pulse,
  isMobile,
  getGameWidth,
  getGameHeight,
  saveToStorage,
  loadFromStorage,
  removeFromStorage
};
